import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import { Plus } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { gql } from '~/__generated__'

import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/ui/dialog'
import { Button } from '~/ui/button'
import { GraphqlError } from '~/ui/errors'
import { toast } from '~/hooks/use-toast'
import { PlaylistCards } from '~/components/playlist-cards'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { playlistPath } from '~/common/paths'
import { useViewer } from '~/auth/use-viewer'
import { useCurrentOrganization } from '~/auth/use-current-organization'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/tabs'
import { AIPlaylistForm } from './ai-playlist-form'
import { SpotifyImportOption } from '~/components/spotify-import-option'
import { SpotifyConnectionCard } from '~/components/spotify-connection-card'

export const playlistsQuery = gql(`
  query Playlists($first: Int = 20, $after: String, $organizationId: ID) {
    playlists(first: $first, after: $after, organizationId: $organizationId) {
      edges {
        node {
          id
          name
          playlistType
          tracksCount
          createdAt
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

export const createPlaylistMutation = gql(`
  mutation CreatePlaylist($input: CreatePlaylistInput!) {
    createPlaylist(input: $input) {
      playlist {
        id
        name
        playlistType
        tracksCount
        createdAt
        aiGenerated
        aiPrompt
      }
    }
  }
`)

export const createStationMutation = gql(`
  mutation CreateStation($input: CreateStationInput!) {
    createStation(input: $input) {
      playlist {
        id
        name
        playlistType
        tracksCount
        createdAt
        aiGenerated
        aiPrompt
      }
      errors
    }
  }
`)

// Spotify mutations for integration
const spotifyAuthorizationUrlMutation = gql(`
  mutation SpotifyAuthorizationUrl($input: SpotifyAuthorizationUrlInput!) {
    spotifyAuthorizationUrl(input: $input) {
      authorizationUrl
      state
    }
  }
`)

const spotifyConnectCallbackMutation = gql(`
  mutation SpotifyConnectCallback($input: SpotifyConnectCallbackInput!) {
    spotifyConnectCallback(input: $input) {
      success
      user {
        id
        spotifyConnected
        spotifyId
      }
      error
    }
  }
`)

const fetchSpotifyPlaylistsMutation = gql(`
  mutation FetchSpotifyPlaylists($input: FetchSpotifyPlaylistsInput!) {
    fetchSpotifyPlaylists(input: $input) {
      success
      playlists {
        spotifyId
        name
        description
        tracksTotal
        public
        collaborative
        images {
          url
          width
          height
        }
        owner {
          spotifyId
          displayName
        }
      }
      error
      requiresReauth
    }
  }
`)

const importSpotifyPlaylistMutation = gql(`
  mutation ImportSpotifyPlaylistOnPlaylistsScreen($input: ImportSpotifyPlaylistInput!) {
    importSpotifyPlaylist(input: $input) {
      success
      playlist {
        id
        name
        isSpotifyPlaylist
        tracksCount
      }
      error
      requiresReauth
    }
  }
`)

export const PlaylistsScreen = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { viewer, refetch: refetchViewer } = useViewer()
  const { id: organizationId } = useCurrentOrganization()
  const [newPlaylistDialogOpen, setNewPlaylistDialogOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('create')
  const [libraryTab, setLibraryTab] = useState<'playlists' | 'stations'>('playlists')
  const { data, error } = useQuery(playlistsQuery, {
    variables: { organizationId: organizationId ?? undefined },
    skip: !organizationId,
  })
  const [createPlaylist] = useSafeMutation(createPlaylistMutation)
  const [createStation] = useSafeMutation(createStationMutation)

  // Spotify state
  const [spotifyPlaylists, setSpotifyPlaylists] = useState<any[]>([])
  const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importingPlaylistId, setImportingPlaylistId] = useState<string | null>(null)

  // Spotify mutations
  const [getAuthUrl] = useSafeMutation(spotifyAuthorizationUrlMutation)
  const [handleCallback] = useSafeMutation(spotifyConnectCallbackMutation)
  const [fetchPlaylists] = useSafeMutation(fetchSpotifyPlaylistsMutation)
  const [importPlaylist] = useSafeMutation(importSpotifyPlaylistMutation)

  // Handle OAuth callback on component mount
  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const storedState = sessionStorage.getItem('spotify_state')
    const error = searchParams.get('error')

    if (error) {
      toast({
        title: 'Spotify Connection Failed',
        description: `Error: ${error}`,
        variant: 'destructive',
      })
      return
    }

    if (code && state && storedState === state) {
      handleSpotifyCallback(code, state)
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [searchParams.get('code'), searchParams.get('state'), searchParams.get('error')])

  if (error) {
    return <GraphqlError error={error} />
  }

  // Handle OAuth callback
  const handleSpotifyCallback = useCallback(
    async (code: string, state: any) => {
      try {
        setIsConnecting(true)

        const result = await handleCallback({
          variables: { input: { code, state } },
        })

        if (result.data?.spotifyConnectCallback?.success) {
          await refetchViewer()
          sessionStorage.removeItem('spotify_state')
          toast({
            title: 'Connected to Spotify',
            description: 'Successfully connected your Spotify account!',
          })
        } else {
          toast({
            title: 'Connection Failed',
            description:
              result.data?.spotifyConnectCallback?.error || 'Failed to connect to Spotify',
            variant: 'destructive',
          })
        }
      } catch (err) {
        console.error('Callback error:', err)
        toast({
          title: 'Connection Error',
          description: 'An error occurred while connecting to Spotify',
          variant: 'destructive',
        })
      } finally {
        setIsConnecting(false)
      }
    },
    [handleCallback, refetchViewer]
  )

  // Spotify helper functions
  async function handleSpotifyConnect() {
    try {
      setIsConnecting(true)

      const result = await getAuthUrl({
        variables: { input: {} },
      })

      if (result.data?.spotifyAuthorizationUrl?.authorizationUrl) {
        // Store state for callback verification
        if (result.data.spotifyAuthorizationUrl.state) {
          sessionStorage.setItem('spotify_state', result.data.spotifyAuthorizationUrl.state)
        }
        // Redirect to Spotify authorization
        window.location.href = result.data.spotifyAuthorizationUrl.authorizationUrl
      } else {
        toast({
          title: 'Error',
          description: 'Failed to get Spotify authorization URL',
          variant: 'destructive',
        })
      }
    } catch (err) {
      console.error('Connection error:', err)
      toast({
        title: 'Error',
        description: 'Failed to connect to Spotify',
        variant: 'destructive',
      })
    } finally {
      setIsConnecting(false)
    }
  }

  const loadSpotifyPlaylists = useCallback(async () => {
    if (!viewer?.spotifyConnected) return

    try {
      setIsLoadingPlaylists(true)

      const result = await fetchPlaylists({
        variables: { input: {} },
      })

      if (result.data?.fetchSpotifyPlaylists?.success) {
        const playlists = result.data.fetchSpotifyPlaylists.playlists || []
        setSpotifyPlaylists(playlists)
      } else {
        const error = result.data?.fetchSpotifyPlaylists?.error
        const requiresReauth = result.data?.fetchSpotifyPlaylists?.requiresReauth

        if (requiresReauth) {
          toast({
            title: 'Connection Expired',
            description: 'Your Spotify connection has expired. Please reconnect.',
            variant: 'destructive',
          })
        } else {
          toast({
            title: 'Error',
            description: error || 'Failed to load playlists',
            variant: 'destructive',
          })
        }
      }
    } catch (err) {
      console.error('Load playlists error:', err)
      toast({
        title: 'Error',
        description: 'Failed to load Spotify playlists',
        variant: 'destructive',
      })
    } finally {
      setIsLoadingPlaylists(false)
    }
  }, [viewer?.spotifyConnected, fetchPlaylists])

  async function handleSpotifyImport(spotifyPlaylistId: string, customName: string | undefined) {
    try {
      setIsImporting(true)
      setImportingPlaylistId(spotifyPlaylistId)

      const result = await importPlaylist({
        variables: {
          input: {
            spotifyPlaylistId,
            name: customName || undefined,
          },
        },
      })

      if (result.data?.importSpotifyPlaylist?.success) {
        // Find the playlist name from the original Spotify playlists list
        const originalPlaylist = spotifyPlaylists.find(
          (p: any) => p.spotifyId === spotifyPlaylistId
        )
        const playlistName = customName || originalPlaylist?.name || 'Playlist'

        toast({
          title: 'Import Started',
          description: `"${playlistName}" is being imported. This may take a few minutes.`,
        })

        // Remove imported playlist from list
        setSpotifyPlaylists((prev) => prev.filter((p: any) => p.spotifyId !== spotifyPlaylistId))

        // Close modal - playlist will be created by background job
        setNewPlaylistDialogOpen(false)
      } else {
        const error = result.data?.importSpotifyPlaylist?.error
        const requiresReauth = result.data?.importSpotifyPlaylist?.requiresReauth

        if (requiresReauth) {
          toast({
            title: 'Connection Expired',
            description: 'Your Spotify connection has expired. Please reconnect.',
            variant: 'destructive',
          })
        } else {
          toast({
            title: 'Import Failed',
            description: error || 'Failed to import playlist',
            variant: 'destructive',
          })
        }
      }
    } catch (err) {
      console.error('Import error:', err)
      toast({
        title: 'Error',
        description: 'Failed to import playlist',
        variant: 'destructive',
      })
    } finally {
      setIsImporting(false)
      setImportingPlaylistId(null)
    }
  }

  // Load playlists when connected and modal opens
  useEffect(() => {
    if (viewer?.spotifyConnected && newPlaylistDialogOpen && activeTab === 'import') {
      loadSpotifyPlaylists()
    }
  }, [viewer?.spotifyConnected, newPlaylistDialogOpen, activeTab, loadSpotifyPlaylists])

  const all = data?.playlists.edges.map((edge: any) => edge.node) ?? []
  const playlists = all.filter((p: any) => p.playlistType !== 'STATION')
  const stations = all.filter((p: any) => p.playlistType === 'STATION')
  // const pageInfo = data?.playlists.pageInfo

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center justify-between px-7 py-3">
        <h1 className="text-2xl">Playlists & Stations</h1>
        <div className="flex gap-2">
          <Dialog open={newPlaylistDialogOpen} onOpenChange={setNewPlaylistDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="default" size="sm" className="cursor-pointer">
                <Plus className="h-4 w-4 md:mr-2" />
                <span className="hidden text-sm md:block">New</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[900px]">
              <DialogHeader>
                <DialogTitle>Create</DialogTitle>
              </DialogHeader>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="create">Playlist</TabsTrigger>
                  <TabsTrigger value="station">Station</TabsTrigger>
                  <TabsTrigger value="import">Spotify Import</TabsTrigger>
                </TabsList>

                <TabsContent value="create" className="mt-6">
                  <AIPlaylistForm
                    onSubmit={async (data) => {
                      const response = await createPlaylist({
                        variables: {
                          input: {
                            name: data.name,
                            aiGenerated: data.ai_generated,
                            aiPrompt: data.ai_prompt,
                            organizationId: organizationId ?? undefined,
                          },
                        },
                      })

                      if (response.errors) {
                        console.error('Error creating playlist:', response.errors)
                        toast({
                          title: 'Error',
                          description: 'Failed to create playlist',
                          variant: 'destructive',
                        })
                        return { playlistId: null }
                      }

                      console.log('Playlist created successfully:', response)

                      if (response.data?.createPlaylist?.playlist?.id) {
                        // For AI playlists, show toast but don't navigate immediately
                        if (data.ai_generated) {
                          toast({
                            title: 'Generating Playlist',
                            description:
                              "Your AI playlist is being generated. You'll be notified when it's ready!",
                          })
                        }

                        // Navigate after a short delay to allow for status subscription setup
                        const playlistId = response.data?.createPlaylist?.playlist?.id
                        if (playlistId) {
                          setTimeout(
                            () => {
                              navigate(playlistPath({ playlistId }))
                            },
                            data.ai_generated ? 1000 : 0
                          )
                        }
                      }

                      return { playlistId: response.data?.createPlaylist?.playlist?.id ?? null }
                    }}
                    isLoading={false}
                    onClose={() => setNewPlaylistDialogOpen(false)}
                  />
                </TabsContent>

                <TabsContent value="station" className="mt-6">
                  <AIPlaylistForm
                    onSubmit={async (data) => {
                      const response = await createStation({
                        variables: {
                          input: {
                            name: data.name,
                            aiPrompt: data.ai_prompt ?? '',
                            organizationId: organizationId ?? undefined,
                          },
                        },
                      })

                      if (response.data?.createStation?.errors?.length) {
                        toast({
                          title: 'Error',
                          description: response.data.createStation.errors.join(', '),
                          variant: 'destructive',
                        })
                        return { playlistId: null }
                      }

                      const station = response.data?.createStation?.playlist
                      if (station?.id) {
                        toast({
                          title: 'Creating station',
                          description: "We're generating preview tracks now. You can start using it immediately.",
                        })
                        setTimeout(() => navigate(playlistPath({ playlistId: station.id })), 250)
                      }

                      return { playlistId: station?.id ?? null }
                    }}
                    isLoading={false}
                    onClose={() => setNewPlaylistDialogOpen(false)}
                  />
                </TabsContent>

                <TabsContent value="import" className="mt-6">
                  <div className="space-y-4">
                    {!viewer?.spotifyConnected ? (
                      <SpotifyConnectionCard
                        isConnected={false}
                        onConnect={handleSpotifyConnect}
                        onDisconnect={() => {}}
                        isLoading={isConnecting}
                      />
                    ) : (
                      <SpotifyImportOption
                        isSpotifyConnected={true}
                        availablePlaylists={spotifyPlaylists}
                        isLoadingPlaylists={isLoadingPlaylists}
                        onConnectSpotify={handleSpotifyConnect}
                        onImportPlaylist={handleSpotifyImport}
                        onRefreshPlaylists={loadSpotifyPlaylists}
                        isImporting={isImporting}
                        importingPlaylistId={importingPlaylistId}
                      />
                    )}

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => setNewPlaylistDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                    </DialogFooter>
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <hr className="border-0 border-b" />
      <div className="flex flex-1 flex-col p-7">
        <Tabs value={libraryTab} onValueChange={(v) => setLibraryTab(v as any)}>
          <TabsList>
            <TabsTrigger value="playlists">Playlists</TabsTrigger>
            <TabsTrigger value="stations">Stations</TabsTrigger>
          </TabsList>
          <TabsContent value="playlists" className="mt-6">
            <PlaylistCards playlists={playlists} />
          </TabsContent>
          <TabsContent value="stations" className="mt-6">
            <PlaylistCards playlists={stations} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
